import React, { useState, useCallback } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import OKCancelButtons from "components/modals/OKCancelButtons";
import UserSearch from "components/input/UserSearch.component";
import Comment from "components/input/Comment.component";
import { requestReview } from "components/modals/modal.actions";
import { useDispatch, useSelector } from "react-redux";
import { RequestReviewActionData, RequestReviewProps } from "types/modals";
import { getTaskUserDescription } from "utils/valueFormatter.function";
import { FormField } from "components/common/FormField.component";
import { Store } from "redux";
import * as reduxSelectors from "components/store/application.reducers";

interface RecipientOption {
    value: string;
    label: string;
}

const RequestReview = ({ more, closeModal, loopFocus, errorCallback }: RequestReviewProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const initComment = useSelector((store: Store) => reduxSelectors.getTaskDraftCommentText(store));

    const [refresh, setRefresh] = useState(false);
    const [recipient, setRecipient] = useState<RecipientOption | null>(null);
    const [comment, setComment] = useState(initComment);
    const [actionRunning, setActionRunning] = useState(false);

    const handleReviewSuccess = useCallback(() => {
        more.handledTaskCallback(more?.tasks[0]?.key, more.navigate);
        closeModal();
    }, [more, closeModal]);

    const handleReviewError = useCallback((error: Record<string, any>) => {
        setActionRunning(false);
        errorCallback?.(error);
    }, [errorCallback]);

    const handleSendForReview = useCallback(() => {
        if (!comment || !recipient?.value) return;

        const currentTask = more?.tasks[0];
        if (!currentTask) return;

        setActionRunning(true);
        const data: RequestReviewActionData = {
            key: currentTask.key,
            comment,
            recipient: recipient.value,
            taskDescription: getTaskUserDescription(currentTask),
        };

        dispatch(requestReview(data, handleReviewSuccess, handleReviewError));
    }, [comment, recipient, more, dispatch, handleReviewSuccess, handleReviewError]);

    const handleRecipientChange = useCallback((value: RecipientOption | null) => {
        setRecipient(value);
        setRefresh(false);
        setActionRunning(false);
    }, []);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setRefresh(false);
    }, []);

    const okEnabled = !!(comment && recipient && !actionRunning);

    return (
        <>
            <div className="ga-modal__content">
                <FormField
                    id="reviewer"
                    label={translate("popUp.sendforreview.reviewer")}
                    labelState={`(${translate("popUp.required")})`}
                >
                    <UserSearch
                        propagateValue={handleRecipientChange}
                        tabindex={MODAL_CONTENT_TAB_INDEX}
                        refresh={refresh}
                        taskId={more?.tasks[0]?.key}
                        ariaLabel={translate("popUp.sendforreview.reviewer")}
                        placeholder={translate("userSearch.placeholder")}
                        value={recipient}
                        translate={translate}
                        releaseFocus={() => setRefresh(false)}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                    />
                </FormField>

                <Comment
                    inputHint={translate("popUp.sendforreview.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                    id="comment"
                    propagateValue={handleCommentChange}
                    value={comment}
                    label={translate("popUp.sendforreview.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleSendForReview}
                okText={translate("popUp.sendforreview.sendForReview")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.close")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default RequestReview;
